/**
 * AME System Deflection Calculator V2 — Guided Edition
 * Built for non-technical buyers: kg/N units, use-case presets, plain-English
 * verdicts with profile suggestions, sag guideline meter, visual profile picker.
 * Same engineering core as V1 (AME-SYS-A014): six load scenarios, self-weight
 * included (d = dg + dF), bending stress vs 195 N/mm² yield point.
 */

(function () {
  'use strict';

  // ============ CONSTANTS ============
  const E_ALUMINIUM = 70000;   // Young's modulus, N/mm²
  const GRAVITY = 9.81;        // m/s²
  const YIELD_STRENGTH = 195;  // N/mm²
  const BASE_URL = 'https://amesystem.com.au';
  const SAG_GENERAL = 250;     // guideline: max sag L/250 for general use
  const SAG_PRECISION = 500;   // guideline: max sag L/500 for precision work
  const SAG_SCALE_MAX = 100;   // sag meter scale ends at L/100

  const REDUCED_MOTION = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  // ============ PROFILE DATABASE ============
  // w/h in mm, cells = [slots across width, slots across height] for the cross-section icon
  const PROFILES = [
    { id: 'AL-3030', size: '30 × 30', series: '30 Series', w: 30, h: 30, cells: [1, 1], Ix: 2.44, Iy: 2.44, Wx: 1.62, Wy: 1.62, A: 2.79, M: 0.75, url: '/products/30-30-t-slot-aluminium-extrusion-profile' },
    { id: 'AL-3030-90', size: '30 × 30 · 90°', series: '30 Series', w: 30, h: 30, cells: [1, 1], Ix: 2.69, Iy: 2.69, Wx: 1.79, Wy: 1.79, A: 3.12, M: 0.84, url: '/products/30-30-90-t-slot-aluminium-extrusion-profile' },
    { id: 'AL-3040', size: '30 × 40', series: '30 Series', w: 30, h: 40, cells: [1, 1], Ix: 3.72, Iy: 6.19, Wx: 2.48, Wy: 3.09, A: 4.35, M: 1.18, url: '/products/30-40-t-slot-aluminium-extrusion-profile' },
    { id: 'AL-3060', size: '30 × 60', series: '30 Series', w: 30, h: 60, cells: [1, 2], Ix: 4.73, Iy: 17.47, Wx: 3.15, Wy: 5.82, A: 5.16, M: 1.39, url: '/products/30-60-t-slot-aluminium-extrusion-profile' },
    { id: 'AL-3060-90', size: '30 × 60 · 90°', series: '30 Series', w: 30, h: 60, cells: [1, 2], Ix: 5.53, Iy: 20.44, Wx: 3.68, Wy: 6.81, A: 5.63, M: 1.52, url: '/products/30-60-90-t-slot-aluminium-extrusion-profile' },
    { id: 'AL-3080', size: '30 × 80', series: '30 Series', w: 30, h: 80, cells: [1, 3], Ix: 11.73, Iy: 27.47, Wx: 7.82, Wy: 6.86, A: 6.10, M: 1.67, url: '/products/30-80-t-slot-aluminium-extrusion-profile' },
    { id: 'AL-6060', size: '60 × 60', series: '30 Series', w: 60, h: 60, cells: [2, 2], Ix: 30.52, Iy: 30.52, Wx: 10.84, Wy: 10.84, A: 8.03, M: 2.17, url: '/products/60-60-t-slot-aluminium-extrusion-profile' },
    { id: 'AL-4040', size: '40 × 40', series: '40 Series', w: 40, h: 40, cells: [1, 1], Ix: 8.16, Iy: 8.16, Wx: 4.08, Wy: 4.08, A: 5.84, M: 1.58, url: '/products/40-40-t-slot-aluminium-extrusion-profile' },
    { id: 'AL-4040-90', size: '40 × 40 · 90°', series: '40 Series', w: 40, h: 40, cells: [1, 1], Ix: 8.82, Iy: 8.82, Wx: 4.41, Wy: 4.41, A: 6.18, M: 1.67, url: '/products/40-40-90-t-slot-aluminium-extrusion-profile' },
    { id: 'AL-4080', size: '40 × 80', series: '40 Series', w: 40, h: 80, cells: [1, 2], Ix: 17.94, Iy: 63.45, Wx: 8.97, Wy: 15.86, A: 11.37, M: 3.07, url: '/products/40-80-t-slot-aluminium-extrusion-profile' },
    { id: 'AL-4080-90', size: '40 × 80 · 90°', series: '40 Series', w: 40, h: 80, cells: [1, 2], Ix: 20.98, Iy: 74.24, Wx: 10.49, Wy: 18.56, A: 11.89, M: 3.41, url: '/products/40-80-90-t-slot-aluminium-extrusion-profile' },
    { id: 'AL-8080', size: '80 × 80', series: '40 Series', w: 80, h: 80, cells: [2, 2], Ix: 125.49, Iy: 125.49, Wx: 31.37, Wy: 31.37, A: 18.45, M: 5.00, url: '/products/80-80-t-slot-aluminium-extrusion-profile' }
  ];

  // ============ LOAD SCENARIOS (per AME-SYS-A014) ============
  // Array order drives the scenario card column and the PDF report table.
  const SCENARIOS = [
    {
      id: 'dist-supported', group: 'distributed', support: 'supported',
      groupName: 'Spread-out load', name: 'On two supports',
      dfCoef: 5 / 384, dgCoef: 5 / 384, stressK: 8,
      dfFormula: 'd<sub>F</sub> = 5·F·L³ / (384·E·I·10⁴)',
      dgFormula: 'd<sub>g</sub> = 5·F<sub>g</sub>·L³ / (384·E·I·10⁴)'
    },
    {
      id: 'dist-fixed-both', group: 'distributed', support: 'fixed-both',
      groupName: 'Spread-out load', name: 'Fixed at both ends',
      dfCoef: 1 / 384, dgCoef: 1 / 384, stressK: 12,
      dfFormula: 'd<sub>F</sub> = F·L³ / (384·E·I·10⁴)',
      dgFormula: 'd<sub>g</sub> = F<sub>g</sub>·L³ / (384·E·I·10⁴)'
    },
    {
      id: 'dist-fixed-one', group: 'distributed', support: 'fixed-one',
      groupName: 'Spread-out load', name: 'Fixed at one end',
      dfCoef: 1 / 8, dgCoef: 1 / 8, stressK: 2,
      dfFormula: 'd<sub>F</sub> = F·L³ / (8·E·I·10⁴)',
      dgFormula: 'd<sub>g</sub> = F<sub>g</sub>·L³ / (8·E·I·10⁴)'
    },
    {
      id: 'central-supported', group: 'central', support: 'supported',
      groupName: 'Load in the middle', name: 'On two supports',
      dfCoef: 1 / 48, dgCoef: 5 / 384, stressK: 4,
      dfFormula: 'd<sub>F</sub> = F·L³ / (48·E·I·10⁴)',
      dgFormula: 'd<sub>g</sub> = 5·F<sub>g</sub>·L³ / (384·E·I·10⁴)'
    },
    {
      id: 'central-fixed-both', group: 'central', support: 'fixed-both',
      groupName: 'Load in the middle', name: 'Fixed at both ends',
      dfCoef: 1 / 192, dgCoef: 1 / 384, stressK: 8,
      dfFormula: 'd<sub>F</sub> = F·L³ / (192·E·I·10⁴)',
      dgFormula: 'd<sub>g</sub> = F<sub>g</sub>·L³ / (384·E·I·10⁴)'
    },
    {
      id: 'central-fixed-one', group: 'central', support: 'fixed-one',
      groupName: 'Load at the tip', name: 'Fixed at one end',
      dfCoef: 1 / 3, dgCoef: 1 / 8, stressK: 1,
      dfFormula: 'd<sub>F</sub> = F·L³ / (3·E·I·10⁴)',
      dgFormula: 'd<sub>g</sub> = F<sub>g</sub>·L³ / (8·E·I·10⁴)'
    }
  ];

  const SUPPORTS = [
    { key: 'supported', label: 'On two supports', hint: 'Resting on a support at each end — like a shelf' },
    { key: 'fixed-one', label: 'Fixed at one end', hint: 'Bolted at one end only — like a bracket arm' },
    { key: 'fixed-both', label: 'Fixed at both ends', hint: 'Bolted rigidly into a frame at both ends' }
  ];

  const LOAD_TYPES = [
    { key: 'central', label: 'In one spot', hint: 'A single weight pressing on one point' },
    { key: 'distributed', label: 'Spread out', hint: 'Weight spread along the whole length' }
  ];

  // ============ USE-CASE PRESETS ============
  const ICONS = {
    workbench: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M3 10h18M5 10v9M19 10v9M8 10V6h8v4"/></svg>',
    shelves: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M4 3v18M20 3v18M4 9h16M4 15h16"/></svg>',
    cnc: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M5 20V7h14v13M5 9h14M12 9v5"/><circle cx="12" cy="16" r="1.6"/></svg>',
    bracket: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M5 3v18M5 8h12M17 8v3M5 15l7-7"/></svg>',
    machine: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><rect x="4" y="4" width="16" height="16" rx="2"/><path d="M4 12h16M12 4v16"/></svg>',
    custom: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M4 7h16M4 12h16M4 17h16"/><circle cx="9" cy="7" r="1.8"/><circle cx="15" cy="12" r="1.8"/><circle cx="8" cy="17" r="1.8"/></svg>'
  };

  const PRESETS = [
    { id: 'workbench', name: 'Workbench', icon: ICONS.workbench, length: 1800, kg: 100, loadType: 'distributed', support: 'fixed-both' },
    { id: 'shelves', name: 'Shelves', icon: ICONS.shelves, length: 1600, kg: 80, loadType: 'distributed', support: 'fixed-both' },
    { id: 'cnc', name: 'CNC / gantry', icon: ICONS.cnc, length: 1000, kg: 60, loadType: 'central', support: 'fixed-both' },
    { id: 'bracket', name: 'Bracket arm', icon: ICONS.bracket, length: 500, kg: 25, loadType: 'central', support: 'fixed-one' },
    { id: 'machine', name: 'Machine frame', icon: ICONS.machine, length: 1500, kg: 150, loadType: 'distributed', support: 'fixed-both' },
    { id: 'custom', name: 'Custom set-up', icon: ICONS.custom, custom: true }
  ];

  // ============ STATE ============
  const state = {
    profileId: 'AL-4040',
    axis: 'x',
    length: 2000,
    loadValue: 50,
    unit: 'kg',
    loadType: 'central',
    support: 'supported',
    preset: null
  };

  function getProfile(id) { return PROFILES.find(p => p.id === id) || null; }
  function scenarioFor(group, support) { return SCENARIOS.find(s => s.group === group && s.support === support); }
  function scenarioById(id) { return SCENARIOS.find(s => s.id === id) || null; }
  function currentScenario() { return scenarioFor(state.loadType, state.support); }
  function loadN() { return state.unit === 'kg' ? (state.loadValue || 0) * GRAVITY : (state.loadValue || 0); }
  function loadLabel() { return state.unit === 'kg' ? `${state.loadValue} kg` : `${state.loadValue} N`; }
  function fmt(v) { return v.toFixed(1); }

  // ============ ENGINEERING CORE (same as V1) ============
  function computeScenario(scenario, F, L, I, W, M) {
    const L3 = Math.pow(L, 3);
    const EI = E_ALUMINIUM * I * 10000;
    const fg = M * (L / 1000) * GRAVITY;
    const dg = (scenario.dgCoef * fg * L3) / EI;
    const df = (scenario.dfCoef * F * L3) / EI;
    const sigma = (F * L) / (scenario.stressK * W * 1000);
    return { fg, dg, df, total: dg + df, sigma };
  }

  function computeFor(scenario, profile, axis, F, L) {
    const I = axis === 'x' ? profile.Ix : profile.Iy;
    const W = axis === 'x' ? profile.Wx : profile.Wy;
    return computeScenario(scenario, F, L, I, W, profile.M);
  }

  // ============ SVG DIAGRAMS ============
  const CLR = { beam: '#005AB5', load: '#d99e00', defl: '#e74c3c', dim: '#777', support: '#8a8a8a' };

  function svgWall(xa, xb) {
    let out = `<rect x="${xa}" y="14" width="${xb - xa}" height="52" fill="${CLR.support}"/>`;
    for (let y = 16; y <= 56; y += 10) {
      out += `<line x1="${xa + 1}" y1="${y}" x2="${xb - 1}" y2="${y + 8}" stroke="#6d6d6d" stroke-width="1"/>`;
    }
    return out;
  }

  function svgPointLoad(x) {
    return `
      <line x1="${x}" y1="10" x2="${x}" y2="28" stroke="${CLR.load}" stroke-width="2.5"/>
      <polygon points="${x},36 ${x - 4.5},27 ${x + 4.5},27" fill="${CLR.load}"/>
      <text x="${x + 7}" y="17" fill="${CLR.load}" font-size="11" font-weight="bold">F</text>`;
  }

  function svgDistributedLoad(x1, x2) {
    let out = `<line x1="${x1}" y1="12" x2="${x2}" y2="12" stroke="${CLR.load}" stroke-width="1.5"/>`;
    const n = 5;
    for (let i = 0; i < n; i++) {
      const x = x1 + ((x2 - x1) / (n - 1)) * i;
      out += `
        <line x1="${x}" y1="12" x2="${x}" y2="26" stroke="${CLR.load}" stroke-width="2"/>
        <polygon points="${x},33 ${x - 3.5},25 ${x + 3.5},25" fill="${CLR.load}"/>`;
    }
    out += `<text x="${(x1 + x2) / 2 + 8}" y="9" fill="${CLR.load}" font-size="11" font-weight="bold">F</text>`;
    return out;
  }

  function svgDeflectionDim(x, s) {
    const labelY = s >= 12 ? 40 + s / 2 + 4 : 40 + s + 10;
    return `
      <line x1="${x}" y1="41" x2="${x}" y2="${40 + s}" stroke="${CLR.defl}" stroke-width="1.5" stroke-dasharray="3,2"/>
      <line x1="${x - 4}" y1="${40 + s}" x2="${x + 4}" y2="${40 + s}" stroke="${CLR.defl}" stroke-width="1.5"/>
      <text x="${x + 6}" y="${labelY}" fill="${CLR.defl}" font-size="11" font-weight="bold">D</text>`;
  }

  function svgLengthDim(x1, x2) {
    const y = 82;
    return `
      <line x1="${x1}" y1="${y}" x2="${x2}" y2="${y}" stroke="${CLR.dim}" stroke-width="1"/>
      <line x1="${x1}" y1="${y - 5}" x2="${x1}" y2="${y + 5}" stroke="${CLR.dim}" stroke-width="1"/>
      <line x1="${x2}" y1="${y - 5}" x2="${x2}" y2="${y + 5}" stroke="${CLR.dim}" stroke-width="1"/>
      <text x="${(x1 + x2) / 2 - 3}" y="${y + 14}" fill="${CLR.dim}" font-size="11" font-weight="bold">L</text>`;
  }

  function drawDiagram(svg, scenario, deflection) {
    if (!svg) return;
    svg.innerHTML = diagramMarkup(scenario, deflection);
  }

  function diagramMarkup(scenario, deflection) {
    const s = 5 + (Math.min(Math.max(deflection, 0), 40) / 40) * 15;

    let x1, x2;
    const parts = [];

    if (scenario.support === 'supported') {
      x1 = 28; x2 = 172;
      parts.push(`<polygon points="${x1},42 ${x1 - 9},60 ${x1 + 9},60" fill="${CLR.support}"/>`);
      parts.push(`<polygon points="${x2},42 ${x2 - 9},60 ${x2 + 9},60" fill="${CLR.support}"/>`);
    } else if (scenario.support === 'fixed-one') {
      x1 = 24; x2 = 176;
      parts.push(svgWall(10, 24));
    } else {
      x1 = 24; x2 = 176;
      parts.push(svgWall(10, 24));
      parts.push(svgWall(176, 190));
    }

    parts.push(`<line x1="${x1}" y1="40" x2="${x2}" y2="40" stroke="#bbb" stroke-width="1.5" stroke-dasharray="4,3"/>`);
    if (scenario.support === 'fixed-one') {
      parts.push(`<path d="M ${x1} 40 Q ${x1 + (x2 - x1) * 0.55} 40 ${x2} ${40 + s}" stroke="${CLR.beam}" stroke-width="3" fill="none"/>`);
    } else {
      parts.push(`<path d="M ${x1} 40 Q ${(x1 + x2) / 2} ${40 + 2 * s} ${x2} 40" stroke="${CLR.beam}" stroke-width="3" fill="none"/>`);
    }

    if (scenario.group === 'distributed') {
      parts.push(svgDistributedLoad(x1 + 10, x2 - 10));
    } else if (scenario.support === 'fixed-one') {
      parts.push(svgPointLoad(x2 - 12));
    } else {
      parts.push(svgPointLoad((x1 + x2) / 2));
    }

    if (scenario.support === 'fixed-one') {
      parts.push(svgDeflectionDim(x2 + 8, s));
    } else {
      parts.push(svgDeflectionDim((x1 + x2) / 2, s));
    }

    parts.push(svgLengthDim(x1, x2));
    return parts.join('');
  }

  // Animated redraw for the hero diagram
  const animState = {};
  function animateDiagram(svg, scenario, toDef) {
    if (!svg) return;
    const key = svg.id || 'hero';
    const from = key in animState ? animState[key].value : toDef;
    if (animState[key] && animState[key].raf) cancelAnimationFrame(animState[key].raf);
    animState[key] = { value: toDef, raf: null };
    if (REDUCED_MOTION || Math.abs(from - toDef) < 0.01) {
      drawDiagram(svg, scenario, toDef);
      return;
    }
    const t0 = performance.now(), dur = 450;
    function frame(t) {
      const k = Math.min((t - t0) / dur, 1);
      const e = 1 - Math.pow(1 - k, 3);
      drawDiagram(svg, scenario, from + (toDef - from) * e);
      if (k < 1) animState[key].raf = requestAnimationFrame(frame);
    }
    animState[key].raf = requestAnimationFrame(frame);
  }

  // Count-up for the big number
  const countState = {};
  function animateNumber(el, toVal) {
    if (!el) return;
    const key = el.id || 'n';
    const from = key in countState ? countState[key].value : toVal;
    if (countState[key] && countState[key].raf) cancelAnimationFrame(countState[key].raf);
    countState[key] = { value: toVal, raf: null };
    if (REDUCED_MOTION || Math.abs(from - toVal) < 0.05) {
      el.textContent = fmt(toVal);
      return;
    }
    const t0 = performance.now(), dur = 450;
    function frame(t) {
      const k = Math.min((t - t0) / dur, 1);
      const e = 1 - Math.pow(1 - k, 3);
      el.textContent = fmt(from + (toVal - from) * e);
      if (k < 1) countState[key].raf = requestAnimationFrame(frame);
    }
    countState[key].raf = requestAnimationFrame(frame);
  }

  // ============ CROSS-SECTION ICON ============
  // Real cross-section images extracted from the AME datasheets, with the
  // hand-drawn SVG kept as a fallback (used if an image fails to load or
  // no asset base is defined by the section).
  function profileIcon(p) {
    if (typeof window.DCV2_ASSET_BASE === 'string') {
      return `<img src="${window.DCV2_ASSET_BASE}dcv2-profile-${p.id.toLowerCase()}.png" alt="${p.id} cross-section" loading="lazy" data-profile="${p.id}" onerror="window.dcv2IconFallback && window.dcv2IconFallback(this)">`;
    }
    return profileIconSVG(p);
  }

  window.dcv2IconFallback = function (img) {
    const p = getProfile(img.dataset.profile);
    if (p) img.outerHTML = profileIconSVG(p);
  };

  function profileIconSVG(p) {
    const scale = 1.35;
    const w = p.w * scale, h = p.h * scale, pad = 6;
    const W = w + pad * 2, H = h + pad * 2;
    const nx = p.cells[0], ny = p.cells[1];
    const cw = w / nx, ch = h / ny;
    const notch = Math.min(cw, ch) * 0.17;
    let out = `<svg viewBox="0 0 ${W} ${H}" class="dcv2-xsec" aria-hidden="true">`;
    out += `<rect x="${pad}" y="${pad}" width="${w}" height="${h}" rx="4" fill="#eaf2fb" stroke="#005AB5" stroke-width="2.4"/>`;
    for (let i = 1; i < nx; i++) out += `<line x1="${pad + cw * i}" y1="${pad}" x2="${pad + cw * i}" y2="${pad + h}" stroke="#9db9d8" stroke-width="1.2"/>`;
    for (let j = 1; j < ny; j++) out += `<line x1="${pad}" y1="${pad + ch * j}" x2="${pad + w}" y2="${pad + ch * j}" stroke="#9db9d8" stroke-width="1.2"/>`;
    for (let i = 0; i < nx; i++) {
      for (let j = 0; j < ny; j++) {
        out += `<circle cx="${pad + cw * (i + 0.5)}" cy="${pad + ch * (j + 0.5)}" r="${Math.min(cw, ch) * 0.14}" fill="none" stroke="#005AB5" stroke-width="1.8"/>`;
      }
    }
    for (let i = 0; i < nx; i++) {
      const x = pad + cw * (i + 0.5);
      out += `<rect x="${x - notch / 2}" y="${pad - 1.2}" width="${notch}" height="${notch * 1.5}" fill="#fff" stroke="#005AB5" stroke-width="1.6"/>`;
      out += `<rect x="${x - notch / 2}" y="${pad + h - notch * 1.5 + 1.2}" width="${notch}" height="${notch * 1.5}" fill="#fff" stroke="#005AB5" stroke-width="1.6"/>`;
    }
    for (let j = 0; j < ny; j++) {
      const y = pad + ch * (j + 0.5);
      out += `<rect x="${pad - 1.2}" y="${y - notch / 2}" width="${notch * 1.5}" height="${notch}" fill="#fff" stroke="#005AB5" stroke-width="1.6"/>`;
      out += `<rect x="${pad + w - notch * 1.5 + 1.2}" y="${y - notch / 2}" width="${notch * 1.5}" height="${notch}" fill="#fff" stroke="#005AB5" stroke-width="1.6"/>`;
    }
    out += '</svg>';
    return out;
  }

  // ============ SMALL PICKER ICONS ============
  function supportIcon(key) {
    if (key === 'supported') {
      return `<svg viewBox="0 0 64 30" fill="none"><path d="M8 10 Q32 17 56 10" stroke="#005AB5" stroke-width="2.5"/><polygon points="11,12 5,22 17,22" fill="#8a8a8a"/><polygon points="53,12 47,22 59,22" fill="#8a8a8a"/></svg>`;
    }
    if (key === 'fixed-one') {
      return `<svg viewBox="0 0 64 30" fill="none"><rect x="4" y="3" width="7" height="24" fill="#8a8a8a"/><path d="M11 10 Q36 10 56 17" stroke="#005AB5" stroke-width="2.5"/></svg>`;
    }
    return `<svg viewBox="0 0 64 30" fill="none"><rect x="4" y="3" width="7" height="24" fill="#8a8a8a"/><rect x="53" y="3" width="7" height="24" fill="#8a8a8a"/><path d="M11 10 Q32 17 53 10" stroke="#005AB5" stroke-width="2.5"/></svg>`;
  }

  function loadTypeIcon(key) {
    if (key === 'central') {
      return `<svg viewBox="0 0 64 30" fill="none"><line x1="32" y1="3" x2="32" y2="14" stroke="#d99e00" stroke-width="2.5"/><polygon points="32,20 27.5,13 36.5,13" fill="#d99e00"/><line x1="8" y1="25" x2="56" y2="25" stroke="#005AB5" stroke-width="2.5"/></svg>`;
    }
    let arrows = '';
    [14, 26, 38, 50].forEach(x => {
      arrows += `<line x1="${x}" y1="6" x2="${x}" y2="13" stroke="#d99e00" stroke-width="2"/><polygon points="${x},18 ${x - 3.5},12 ${x + 3.5},12" fill="#d99e00"/>`;
    });
    return `<svg viewBox="0 0 64 30" fill="none"><line x1="14" y1="4" x2="50" y2="4" stroke="#d99e00" stroke-width="1.5"/>${arrows}<line x1="8" y1="25" x2="56" y2="25" stroke="#005AB5" stroke-width="2.5"/></svg>`;
  }

  function orientationIcon(axis) {
    if (axis === 'x') {
      return `<svg viewBox="0 0 40 34" fill="none"><line x1="20" y1="2" x2="20" y2="10" stroke="#d99e00" stroke-width="2"/><polygon points="20,15 16.5,9 23.5,9" fill="#d99e00"/><rect x="7" y="18" width="26" height="12" rx="2" fill="#eaf2fb" stroke="#005AB5" stroke-width="2"/></svg>`;
    }
    return `<svg viewBox="0 0 40 34" fill="none"><line x1="20" y1="1" x2="20" y2="6" stroke="#d99e00" stroke-width="2"/><polygon points="20,11 16.5,5 23.5,5" fill="#d99e00"/><rect x="14" y="14" width="12" height="19" rx="2" fill="#eaf2fb" stroke="#005AB5" stroke-width="2"/></svg>`;
  }

  // ============ UI BUILDERS ============
  function buildPresets() {
    const wrap = document.getElementById('dcv2-presets');
    if (!wrap) return;
    wrap.innerHTML = PRESETS.map(p => `
      <button type="button" class="dcv2-preset" data-preset="${p.id}">
        <span class="dcv2-preset-icon">${p.icon}</span>
        <span class="dcv2-preset-name">${p.name}</span>
      </button>`).join('');
  }

  function buildProfilePicker() {
    const wrap = document.getElementById('dcv2-profile-picker');
    if (!wrap) return;
    const series = [...new Set(PROFILES.map(p => p.series))];
    wrap.innerHTML = series.map(s => `
      <div class="dcv2-series-label">${s}</div>
      <div class="dcv2-profile-grid">
        ${PROFILES.filter(p => p.series === s).map(p => `
          <button type="button" class="dcv2-profile-card" data-profile="${p.id}" title="${p.id} — ${p.size} mm, ${p.M} kg/m">
            <span class="dcv2-profile-icon">${profileIcon(p)}</span>
            <span class="dcv2-profile-name">${p.id}</span>
            <span class="dcv2-profile-size">${p.size.replace(/ /g, '')} mm</span>
          </button>`).join('')}
      </div>`).join('');
  }

  function buildLoadTypePicker() {
    const wrap = document.getElementById('dcv2-loadtype');
    if (!wrap) return;
    wrap.innerHTML = LOAD_TYPES.map(t => `
      <button type="button" class="dcv2-pick" data-loadtype="${t.key}">
        <span class="dcv2-pick-icon">${loadTypeIcon(t.key)}</span>
        <span class="dcv2-pick-label">${t.label}</span>
        <span class="dcv2-pick-hint">${t.hint}</span>
      </button>`).join('');
  }

  function buildSupportPicker() {
    const wrap = document.getElementById('dcv2-supports');
    if (!wrap) return;
    wrap.innerHTML = SUPPORTS.map(s => `
      <button type="button" class="dcv2-pick" data-support="${s.key}">
        <span class="dcv2-pick-icon">${supportIcon(s.key)}</span>
        <span class="dcv2-pick-label">${s.label}</span>
        <span class="dcv2-pick-hint">${s.hint}</span>
      </button>`).join('');
  }

  function buildOrientationPicker() {
    const wrap = document.getElementById('dcv2-orientation');
    if (!wrap) return;
    wrap.innerHTML = [
      { axis: 'x', label: 'Flat (horizontal)' },
      { axis: 'y', label: 'Upright (vertical)' }
    ].map(o => `
      <button type="button" class="dcv2-orient" data-axis="${o.axis}">
        <span class="dcv2-orient-icon">${orientationIcon(o.axis)}</span>
        <span>${o.label}</span>
      </button>`).join('');
  }

  // Grouped like the AME reference spreadsheet: Distributed Load / Central Load
  const SCENARIO_GROUPS = [
    { key: 'distributed', label: 'Distributed Load' },
    { key: 'central', label: 'Central Load' }
  ];

  function buildScenarioGrid() {
    const wrap = document.getElementById('dcv2-grid');
    if (!wrap) return;
    wrap.innerHTML = SCENARIO_GROUPS.map(g => `
      <div class="dcv2-scenario-group">
        <div class="dcv2-scenario-group-title">${g.label}</div>
        <div class="dcv2-scenario-group-cards">
          ${SCENARIOS.filter(sc => sc.group === g.key).map(sc => `
          <button type="button" class="dcv2-scenario-card" data-scenario="${sc.id}">
            <span class="dcv2-scenario-name">${sc.groupName} · ${sc.name}</span>
            <svg viewBox="0 0 200 100" id="dcv2-mini-${sc.id}" aria-hidden="true"></svg>
            <span class="dcv2-scenario-def">D = <strong id="dcv2-grid-def-${sc.id}">–</strong> mm <span class="dcv2-dot" id="dcv2-grid-dot-${sc.id}"></span></span>
          </button>`).join('')}
        </div>
      </div>`).join('');
  }

  // ============ VERDICT & SUGGESTIONS ============
  function assess(r, L) {
    const g250 = L / SAG_GENERAL, g500 = L / SAG_PRECISION, g100 = L / SAG_SCALE_MAX;
    if (r.sigma > YIELD_STRENGTH) {
      return { cls: 'bad', icon: '✗', title: 'Overloaded — risk of permanent bending',
        body: `The bending stress of ${r.sigma.toFixed(1)} N/mm² is above the ${YIELD_STRENGTH} N/mm² yield point of aluminium. The profile could permanently deform. Choose a stronger profile or reduce the load.` };
    }
    if (r.total <= g500) {
      return { cls: 'ok', icon: '✓', title: 'Rock solid',
        body: `Only ${fmt(r.total)} mm of sag over a ${L} mm span — within the precision guideline of ${fmt(g500)} mm (L/500). Suitable even for precision work.` };
    }
    if (r.total <= g250) {
      return { cls: 'good', icon: '✓', title: 'Good to go',
        body: `${fmt(r.total)} mm of sag is within the general-use guideline of ${fmt(g250)} mm (L/250) for this span.` };
    }
    if (r.total <= g100) {
      return { cls: 'warn', icon: '⚠', title: 'Noticeable sag',
        body: `${fmt(r.total)} mm of sag exceeds the recommended maximum of ${fmt(g250)} mm (L/250) for a ${L} mm span. Fine for rough duty — but consider a stronger profile, a shorter span, or an extra support.` };
    }
    return { cls: 'bad', icon: '✗', title: 'Too much sag',
      body: `${fmt(r.total)} mm of sag is far beyond the ${fmt(g250)} mm (L/250) guideline for a ${L} mm span. Choose a stronger profile, add a support, or shorten the span.` };
  }

  function findSuggestion(scenario, profile, result) {
    const L = state.length, F = loadN();
    const target = L / SAG_GENERAL;
    const needsBetter = result.sigma > YIELD_STRENGTH || result.total > target;

    if (needsBetter) {
      const candidates = [];
      PROFILES.forEach(p => {
        ['x', 'y'].forEach(ax => {
          if (p.id === profile.id && ax === state.axis) return;
          if (p.Ix === p.Iy && ax === 'y') return;
          const r = computeFor(scenario, p, ax, F, L);
          if (r.total <= target && r.sigma <= YIELD_STRENGTH) candidates.push({ p, ax, r });
        });
      });
      candidates.sort((a, b) => a.p.M - b.p.M);
      const best = candidates[0];
      if (!best) {
        return `<span class="dcv2-sug-icon">💡</span><span>No single profile stays within the guideline for this span and load. Try a shorter span, an extra support in the middle, or ask the AME System team about doubled-up profiles.</span>`;
      }
      const orient = best.ax === 'y' ? ' mounted upright' : '';
      return `<span class="dcv2-sug-icon">💡</span><span><strong>${best.p.id}</strong>${orient} would sag just <strong>${fmt(best.r.total)} mm</strong> here.</span>
        <span class="dcv2-sug-actions">
          <button type="button" class="dcv2-sug-btn" data-use-profile="${best.p.id}" data-use-axis="${best.ax}">Try it</button>
          <a class="dcv2-sug-link" href="${BASE_URL}${best.p.url}" target="_blank" rel="noopener">View profile →</a>
        </span>`;
    }
    return '';
  }

  // ============ MAIN RENDER ============
  function render(opts) {
    const options = opts || {};
    const profile = getProfile(state.profileId);
    const F = loadN();
    const L = state.length;
    if (!profile || !L || L < 100 || !F || F <= 0) return;

    const scenario = currentScenario();
    const r = computeFor(scenario, profile, state.axis, F, L);
    const isSquare = profile.Ix === profile.Iy;
    if (isSquare) state.axis = 'x';

    // --- selected states on pickers ---
    document.querySelectorAll('.dcv2-profile-card').forEach(b => b.classList.toggle('dcv2-selected', b.dataset.profile === state.profileId));
    document.querySelectorAll('#dcv2-loadtype .dcv2-pick').forEach(b => b.classList.toggle('dcv2-selected', b.dataset.loadtype === state.loadType));
    document.querySelectorAll('#dcv2-supports .dcv2-pick').forEach(b => b.classList.toggle('dcv2-selected', b.dataset.support === state.support));
    document.querySelectorAll('.dcv2-orient').forEach(b => b.classList.toggle('dcv2-selected', !isSquare && b.dataset.axis === state.axis));
    document.querySelectorAll('.dcv2-preset').forEach(b => b.classList.toggle('dcv2-selected', b.dataset.preset === state.preset));

    // orientation stays visible at all times; for square profiles the
    // buttons blank out (visibility) so the layout never jumps
    const orientField = document.getElementById('dcv2-orientation-field');
    if (orientField) orientField.classList.toggle('dcv2-orient-na', isSquare);
    const squareNote = document.getElementById('dcv2-square-note');
    if (squareNote) squareNote.classList.toggle('dcv2-hidden', !isSquare);

    // --- inputs mirror state (when set programmatically) ---
    if (options.syncInputs) {
      const lenInput = document.getElementById('dcv2-length');
      const lenSlider = document.getElementById('dcv2-length-slider');
      const loadInput = document.getElementById('dcv2-load');
      const loadSlider = document.getElementById('dcv2-load-slider');
      if (lenInput) lenInput.value = state.length;
      if (lenSlider) lenSlider.value = Math.min(state.length, parseFloat(lenSlider.max));
      if (loadInput) loadInput.value = state.loadValue;
      if (loadSlider) loadSlider.value = Math.min(state.loadValue, parseFloat(loadSlider.max));
      document.querySelectorAll('.dcv2-unit-btn').forEach(b => b.classList.toggle('dcv2-selected', b.dataset.unit === state.unit));
    }

    // --- hero result panel ---
    const I = state.axis === 'x' ? profile.Ix : profile.Iy;
    const W = state.axis === 'x' ? profile.Wx : profile.Wy;

    const metaEl = document.getElementById('dcv2-res-meta');
    if (metaEl) metaEl.innerHTML = `<strong>${profile.id}</strong> (${profile.size} mm) · I${state.axis} = ${I} cm⁴ · W${state.axis} = ${W} cm³ · ${profile.M} kg/m`;

    const scenLabel = document.getElementById('dcv2-scenario-label');
    if (scenLabel) scenLabel.textContent = `${scenario.groupName} · ${scenario.name}`;

    animateDiagram(document.getElementById('dcv2-hero-diagram'), scenario, r.total);
    animateNumber(document.getElementById('dcv2-def-value'), r.total);

    const legend = document.getElementById('dcv2-hero-legend');
    if (legend) {
      const fPart = state.unit === 'kg' ? `F = ${state.loadValue} kg (${Math.round(F)} N)` : `F = ${state.loadValue} N`;
      legend.textContent = `${fPart} · L = ${L} mm · D = ${fmt(r.total)} mm`;
    }

    // verdict
    const verdict = assess(r, L);
    const vEl = document.getElementById('dcv2-verdict');
    if (vEl) {
      vEl.className = `dcv2-verdict dcv2-verdict-${verdict.cls}`;
      vEl.innerHTML = `<span class="dcv2-verdict-icon">${verdict.icon}</span><span class="dcv2-verdict-text"><strong>${verdict.title}</strong>${verdict.body}</span>`;
    }
    const defValue = document.getElementById('dcv2-def-value');
    if (defValue) defValue.className = `dcv2-def-value dcv2-def-${verdict.cls}`;

    // suggestion
    const sugEl = document.getElementById('dcv2-suggestion');
    if (sugEl) {
      const html = findSuggestion(scenario, profile, r);
      sugEl.innerHTML = html;
      sugEl.classList.toggle('dcv2-hidden', !html);
    }

    // sag meter (scale: 0 → L/100; ticks fixed at 20% = L/500 and 40% = L/250)
    const marker = document.getElementById('dcv2-meter-marker');
    if (marker) {
      const pct = Math.min((r.total / (L / SAG_SCALE_MAX)) * 100, 100);
      marker.style.left = pct + '%';
    }
    const t500 = document.getElementById('dcv2-meter-t500');
    if (t500) t500.textContent = `Precision · L/500 = ${fmt(L / SAG_PRECISION)} mm`;
    const t250 = document.getElementById('dcv2-meter-t250');
    if (t250) t250.textContent = `General use · L/250 = ${fmt(L / SAG_GENERAL)} mm`;

    // stress pill
    const stressEl = document.getElementById('dcv2-stress');
    if (stressEl) {
      const ok = r.sigma <= YIELD_STRENGTH;
      stressEl.className = `dcv2-stress ${ok ? 'dcv2-stress-ok' : 'dcv2-stress-warn'}`;
      stressEl.innerHTML = ok
        ? `✓ Bending stress ${r.sigma.toFixed(1)} N/mm² — safely under the ${YIELD_STRENGTH} N/mm² yield point`
        : `⚠ Bending stress ${r.sigma.toFixed(1)} N/mm² — above the ${YIELD_STRENGTH} N/mm² yield point`;
    }

    // calculation breakdown
    const calcEl = document.getElementById('dcv2-calc');
    if (calcEl) {
      const fLine = state.unit === 'kg'
        ? `<p>Acting force: F = ${state.loadValue} kg × ${GRAVITY} m/s² ≈ <strong>${Math.round(F)} N</strong></p>`
        : `<p>Acting force: F = <strong>${state.loadValue} N</strong></p>`;
      calcEl.innerHTML = `
        ${fLine}
        <p>Weight force: F<sub>g</sub> = M·L·g = ${profile.M} kg/m × ${(L / 1000).toFixed(2)} m × ${GRAVITY} m/s² ≈ <strong>${r.fg.toFixed(2)} N</strong></p>
        <p>Deflection due to own weight: ${scenario.dgFormula} ≈ <strong>${r.dg.toFixed(2)} mm</strong></p>
        <p>Deflection due to the acting force: ${scenario.dfFormula} ≈ <strong>${r.df.toFixed(2)} mm</strong></p>
        <p>Total deflection: D = d<sub>g</sub> + d<sub>F</sub> ≈ <strong>${fmt(r.total)} mm</strong></p>
        <p class="dcv2-calc-note">The final deflection result includes the contribution from the profile's self-weight.</p>`;
    }

    // buy button
    const buyBtn = document.getElementById('dcv2-buy-btn');
    if (buyBtn) buyBtn.href = BASE_URL + profile.url;

    // --- all-scenarios grid ---
    SCENARIOS.forEach(sc => {
      const rr = computeFor(sc, profile, state.axis, F, L);
      const defEl = document.getElementById(`dcv2-grid-def-${sc.id}`);
      if (defEl) defEl.textContent = fmt(rr.total);
      const dotEl = document.getElementById(`dcv2-grid-dot-${sc.id}`);
      if (dotEl) {
        const a = assess(rr, L);
        dotEl.className = `dcv2-dot dcv2-dot-${a.cls}`;
        dotEl.title = a.title;
      }
      drawDiagram(document.getElementById(`dcv2-mini-${sc.id}`), sc, rr.total);
      const card = document.querySelector(`.dcv2-scenario-card[data-scenario="${sc.id}"]`);
      if (card) card.classList.toggle('dcv2-selected', sc.id === scenario.id);
    });

    // --- chart ---
    updateChart(scenario, profile);

    // --- share URL ---
    updateShareURL();
  }

  // ============ CHART ============
  function updateChart(scenario, profile) {
    const canvas = document.getElementById('dcv2-chart');
    if (!canvas) return;
    const F = loadN(), L = state.length;

    const caption = document.getElementById('dcv2-chart-caption');
    if (caption) caption.textContent = `Showing: ${scenario.groupName} · ${scenario.name} · ${profile.id} · ${loadLabel()}`;

    const ctx = canvas.getContext('2d');
    // draw at the actual displayed size (and screen resolution) so the
    // chart never looks stretched when the layout is wider than 1000px
    const width = canvas.clientWidth || 1000;
    const height = canvas.clientHeight || 200;
    if (width < 50) return; // hidden (e.g. other tab active) — draw on next render
    const dpr = window.devicePixelRatio || 1;
    if (canvas.width !== Math.round(width * dpr) || canvas.height !== Math.round(height * dpr)) {
      canvas.width = Math.round(width * dpr);
      canvas.height = Math.round(height * dpr);
    }
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    const padding = { top: 30, right: 20, bottom: 40, left: 52 };
    ctx.clearRect(0, 0, width, height);

    const maxLen = Math.max(5000, L);
    const lengths = [], deflections = [];
    for (let l = 500; l <= maxLen; l += 100) {
      lengths.push(l);
      deflections.push(computeFor(scenario, profile, state.axis, F, l).total);
    }
    const maxDef = Math.max(...deflections);
    const chartWidth = width - padding.left - padding.right;
    const chartHeight = height - padding.top - padding.bottom;

    ctx.strokeStyle = '#e5ecf4';
    ctx.lineWidth = 1;
    for (let i = 0; i <= 5; i++) {
      const y = padding.top + (chartHeight / 5) * i;
      ctx.beginPath(); ctx.moveTo(padding.left, y); ctx.lineTo(width - padding.right, y); ctx.stroke();
    }

    ctx.strokeStyle = '#9db3c8';
    ctx.beginPath();
    ctx.moveTo(padding.left, padding.top);
    ctx.lineTo(padding.left, height - padding.bottom);
    ctx.lineTo(width - padding.right, height - padding.bottom);
    ctx.stroke();

    // guideline band: sag beyond L/250 shaded
    ctx.fillStyle = 'rgba(229,57,53,0.06)';
    for (let i = 0; i < lengths.length - 1; i++) {
      // shade region above the L/250 line by drawing the guideline curve
    }
    ctx.strokeStyle = '#e0a800';
    ctx.setLineDash([5, 4]);
    ctx.lineWidth = 1.5;
    ctx.beginPath();
    lengths.forEach((l, i) => {
      const x = padding.left + (l / maxLen) * chartWidth;
      const y = padding.top + chartHeight - Math.min((l / SAG_GENERAL) / maxDef, 1) * chartHeight;
      if (i === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y);
    });
    ctx.stroke();
    ctx.setLineDash([]);

    ctx.strokeStyle = '#005AB5';
    ctx.lineWidth = 2.5;
    ctx.beginPath();
    lengths.forEach((l, i) => {
      const x = padding.left + (l / maxLen) * chartWidth;
      const y = padding.top + chartHeight - (deflections[i] / maxDef) * chartHeight;
      if (i === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y);
    });
    ctx.stroke();

    const currentDef = computeFor(scenario, profile, state.axis, F, L).total;
    const cx = padding.left + (L / maxLen) * chartWidth;
    const cy = padding.top + chartHeight - (currentDef / maxDef) * chartHeight;
    ctx.fillStyle = '#F2DC20';
    ctx.strokeStyle = '#333';
    ctx.lineWidth = 2;
    ctx.beginPath(); ctx.arc(cx, cy, 6, 0, Math.PI * 2); ctx.fill(); ctx.stroke();

    ctx.fillStyle = '#5b6b7c';
    ctx.font = '11px -apple-system, sans-serif';
    ctx.textAlign = 'center';
    ctx.fillText('Length L (mm)', width / 2, height - 8);
    ctx.save();
    ctx.translate(12, height / 2);
    ctx.rotate(-Math.PI / 2);
    ctx.fillText('Deflection D (mm)', 0, 0);
    ctx.restore();

    ctx.textAlign = 'center';
    for (let i = 1; i <= 5; i++) {
      const val = Math.round((maxLen * i) / 5 / 100) * 100;
      const x = padding.left + (val / maxLen) * chartWidth;
      ctx.fillText(val.toString(), x, height - padding.bottom + 15);
    }
    ctx.textAlign = 'right';
    for (let i = 0; i <= 5; i++) {
      const val = (maxDef / 5) * (5 - i);
      const y = padding.top + (chartHeight / 5) * i;
      ctx.fillText(val.toFixed(1), padding.left - 8, y + 4);
    }

    ctx.fillStyle = '#22303e';
    ctx.font = 'bold 11px -apple-system, sans-serif';
    ctx.textAlign = cx > width - 150 ? 'right' : 'left';
    ctx.fillText(`${L}mm → ${fmt(currentDef)}mm`, cx + (cx > width - 150 ? -10 : 10), cy - 8);

    ctx.fillStyle = '#a8811a';
    ctx.font = '10px -apple-system, sans-serif';
    ctx.textAlign = 'right';
    ctx.fillText('· · · L/250 guideline', width - padding.right - 4, padding.top + 12);
  }

  // ============ SHARE URL ============
  function updateShareURL() {
    const shareInput = document.getElementById('dcv2-share-url');
    if (!shareInput) return;
    const url = new URL(window.location.href.split('?')[0]);
    url.searchParams.set('p', state.profileId);
    url.searchParams.set('l', state.length);
    url.searchParams.set('f', state.loadValue);
    url.searchParams.set('u', state.unit);
    url.searchParams.set('a', state.axis);
    url.searchParams.set('s', currentScenario().id);
    shareInput.value = url.toString();
  }

  function loadFromURL() {
    const params = new URLSearchParams(window.location.search);
    const profileId = params.get('p');
    if (!profileId || !getProfile(profileId)) return false;
    state.profileId = profileId;
    const l = parseFloat(params.get('l'));
    if (l >= 100) state.length = l;
    const f = parseFloat(params.get('f'));
    if (f > 0) state.loadValue = f;
    const u = params.get('u');
    if (u === 'kg' || u === 'n') state.unit = u;
    const a = params.get('a');
    if (a === 'x' || a === 'y') state.axis = a;
    const sc = scenarioById(params.get('s'));
    if (sc) { state.loadType = sc.group; state.support = sc.support; }
    return true;
  }

  function copyShareURL() {
    const shareInput = document.getElementById('dcv2-share-url');
    if (!shareInput) return;
    const flash = (msg) => {
      const btn = document.getElementById('dcv2-copy-btn');
      if (btn) {
        const orig = 'Copy link';
        btn.textContent = msg;
        setTimeout(() => btn.textContent = orig, 2000);
      }
    };
    // navigator.clipboard rejects while another window (e.g. the PDF report)
    // holds focus — fall back to selecting the text + execCommand.
    const fallback = () => {
      let copied = false;
      try {
        shareInput.focus();
        shareInput.select();
        copied = document.execCommand('copy');
      } catch (e) {}
      flash(copied ? '✓ Copied!' : 'Press Ctrl+C');
    };
    const onSuccess = () => flash('✓ Copied!');
    window.focus();
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(shareInput.value).then(onSuccess, fallback);
    } else {
      fallback();
    }
  }

  // ============ PDF EXPORT ============
  const LOGO_URL = 'https://amesystem.com.au/cdn/shop/files/AME_System_logo_black.png';

  function exportPDF() {
    const profile = getProfile(state.profileId);
    const F = loadN(), L = state.length;
    if (!profile || !L || !F) return;

    const I = state.axis === 'x' ? profile.Ix : profile.Iy;
    const W = state.axis === 'x' ? profile.Wx : profile.Wy;
    const loadStr = state.unit === 'kg' ? `${state.loadValue} kg (${Math.round(F)} N)` : `${state.loadValue} N`;
    const orientStr = profile.Ix === profile.Iy ? 'Any (square profile)' : (state.axis === 'x' ? 'Flat (horizontal)' : 'Upright (vertical)');

    const scenario = currentScenario();
    const r = computeFor(scenario, profile, state.axis, F, L);
    const verdict = assess(r, L);
    let preparedFor = '';
    try {
      const rec = JSON.parse(localStorage.getItem('ame-deflection-terms-v2') || 'null');
      if (rec && rec.email) preparedFor = `<div class="date">Prepared for: ${String(rec.email).replace(/[<>&"']/g, '')}</div>`;
    } catch (e) {}
    const vColors = {
      ok: ['#e9f7ec', '#14611f', '#c4e8cc'],
      good: ['#e8f2fd', '#114c86', '#c3ddf5'],
      warn: ['#fff7e0', '#7a5c00', '#f3e3a5'],
      bad: ['#fdecea', '#96201a', '#f6c8c4']
    }[verdict.cls];
    const numColor = { ok: '#1e8e3e', good: '#1a73c0', warn: '#c58a00', bad: '#d93025' }[verdict.cls];
    const stressOk = r.sigma <= YIELD_STRENGTH;
    const fLine = state.unit === 'kg'
      ? `Acting force: F = ${state.loadValue} kg × ${GRAVITY} m/s² ≈ <strong>${Math.round(F)} N</strong>`
      : `Acting force: F = <strong>${state.loadValue} N</strong>`;

    let resultsHTML = '';
    SCENARIO_GROUPS.forEach(g => {
      resultsHTML += `
        <tr>
          <td colspan="6" style="padding:8px 12px;background:#eef4fa;border-bottom:1px solid #d5e2ef;font-weight:800;font-size:11.5px;text-transform:uppercase;letter-spacing:0.5px;color:#114c86;">${g.label}</td>
        </tr>`;
      SCENARIOS.filter(sc => sc.group === g.key).forEach(sc => {
      const rr = computeScenario(sc, F, L, I, W, profile.M);
      const ok = rr.sigma <= YIELD_STRENGTH;
      const selected = sc.id === scenario.id;
      resultsHTML += `
        <tr style="${selected ? 'background:#f2f8ff;' : ''}">
          <td style="padding:10px 12px;border-bottom:1px solid #e0e0e0;font-weight:${selected ? '800' : '600'};">${selected ? '▶ ' : ''}${sc.groupName} – ${sc.name}</td>
          <td style="padding:10px 12px;border-bottom:1px solid #e0e0e0;text-align:center;">${rr.dg.toFixed(2)} mm</td>
          <td style="padding:10px 12px;border-bottom:1px solid #e0e0e0;text-align:center;">${rr.df.toFixed(2)} mm</td>
          <td style="padding:10px 12px;border-bottom:1px solid #e0e0e0;text-align:center;font-weight:700;">${fmt(rr.total)} mm</td>
          <td style="padding:10px 12px;border-bottom:1px solid #e0e0e0;text-align:center;">${rr.sigma.toFixed(1)} N/mm²</td>
          <td style="padding:10px 12px;border-bottom:1px solid #e0e0e0;text-align:center;color:${ok ? '#2e7d32' : '#c62828'}">${ok ? '✓ Within yield' : '⚠ Exceeds yield'}</td>
        </tr>`;
      });
    });

    const printContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Deflection Report - ${profile.id}</title>
        <style>
          * { -webkit-print-color-adjust: exact; print-color-adjust: exact; box-sizing: border-box; }
          body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; padding: 34px 40px; color: #22303e; margin: 0; }
          .header { display: flex; justify-content: space-between; align-items: center; gap: 20px; border-bottom: 3px solid #005AB5; padding-bottom: 16px; margin-bottom: 24px; }
          .header img { height: 42px; display: block; }
          .header-right { text-align: right; }
          .tagline { font-size: 12.5px; font-weight: 700; color: #22303e; }
          .phone { font-size: 13px; font-weight: 800; color: #005AB5; margin-top: 2px; }
          .date { color: #7b8794; font-size: 11.5px; margin-top: 4px; }
          h1 { font-size: 21px; color: #1c2b3a; margin: 0 0 4px; }
          .subtitle { color: #5b6b7c; margin: 0 0 20px; font-size: 13.5px; }
          .params { display: grid; grid-template-columns: repeat(4, 1fr); gap: 12px; margin-bottom: 22px; }
          .param { background: #f6f9fc; border: 1px solid #e3e9f0; padding: 12px 14px; border-radius: 10px; }
          .param-label { font-size: 10.5px; color: #7b8794; text-transform: uppercase; letter-spacing: 0.4px; margin-bottom: 4px; }
          .param-value { font-size: 15px; font-weight: 700; color: #22303e; }
          .param-value small { font-size: 11px; font-weight: 500; color: #7b8794; }
          .hero { display: grid; grid-template-columns: 1fr 1fr; gap: 18px; border: 1.5px solid #e3e9f0; border-radius: 14px; padding: 18px; margin-bottom: 22px; page-break-inside: avoid; }
          .hero-scenario { font-size: 12.5px; font-weight: 800; color: #005AB5; text-align: center; margin-bottom: 6px; }
          .hero-diagram { background: #f6f9fc; border: 1px solid #e3e9f0; border-radius: 10px; padding: 10px; }
          .hero-diagram svg { width: 100%; height: auto; display: block; }
          .hero-legend { font-size: 11px; color: #5b6b7c; text-align: center; margin-top: 6px; }
          .def-row { text-align: center; margin: 4px 0 10px; }
          .def-caption { font-size: 13px; font-weight: 600; color: #5b6b7c; }
          .def-value { font-size: 38px; font-weight: 800; color: ${numColor}; }
          .def-unit { font-size: 16px; font-weight: 700; color: #5b6b7c; }
          .verdict { background: ${vColors[0]}; color: ${vColors[1]}; border: 1px solid ${vColors[2]}; border-radius: 10px; padding: 11px 13px; font-size: 12px; line-height: 1.5; }
          .verdict strong { display: block; font-size: 13.5px; margin-bottom: 2px; }
          .guides { font-size: 11px; color: #5b6b7c; margin-top: 8px; }
          .stress { margin-top: 8px; padding: 8px 12px; border-radius: 8px; font-size: 11.5px; font-weight: 700;
            background: ${stressOk ? '#e9f7ec' : '#fdecea'}; color: ${stressOk ? '#14611f' : '#96201a'}; border: 1px solid ${stressOk ? '#c4e8cc' : '#f6c8c4'}; }
          .calc { background: #f6f9fc; border: 1px solid #e3e9f0; border-radius: 12px; padding: 14px 16px; margin-bottom: 24px; page-break-inside: avoid; }
          .calc h3 { margin: 0 0 8px; font-size: 13.5px; color: #1c2b3a; }
          .calc p { margin: 5px 0; font-size: 11.5px; color: #5b6b7c; line-height: 1.5; }
          .calc p strong { color: #22303e; }
          .calc .note { font-style: italic; color: #8b97a3; }
          h2 { font-size: 15px; color: #1c2b3a; margin: 0 0 10px; }
          table { width: 100%; border-collapse: collapse; margin-bottom: 8px; page-break-inside: avoid; }
          th { background: #005AB5; color: white; padding: 10px 12px; text-align: left; font-size: 12px; }
          td { font-size: 12px; }
          .selfweight-note { font-size: 11px; color: #5b6b7c; margin: 0 0 24px; }
          .specs { padding: 16px 18px; background: #f6f9fc; border: 1px solid #e3e9f0; border-radius: 12px; page-break-inside: avoid; }
          .specs h3 { margin: 0 0 10px; font-size: 13.5px; }
          .specs-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 8px; }
          .spec-item { font-size: 12px; }
          .spec-item strong { color: #005AB5; }
          .footer { margin-top: 28px; padding-top: 16px; border-top: 2px solid #005AB5; font-size: 11px; color: #7b8794; text-align: center; line-height: 1.7; }
          .footer .brand { font-size: 13px; color: #22303e; }
          .footer .brand strong { color: #005AB5; }
          .terms { margin-top: 18px; padding-top: 14px; border-top: 1px solid #e3e9f0; page-break-inside: avoid; }
          .terms h3 { margin: 0 0 6px; font-size: 12px; color: #1c2b3a; }
          .terms p { margin: 0 0 6px; font-size: 10.5px; color: #7b8794; line-height: 1.6; }
          .buy-link { display: inline-block; margin-top: 18px; padding: 11px 24px; background: #005AB5; color: white; text-decoration: none; border-radius: 8px; font-weight: 700; font-size: 13px; }
          @media print { .buy-link { display: none; } }
        </style>
      </head>
      <body>
        <div class="header">
          <img src="${LOGO_URL}" alt="AME System">
          <div class="header-right">
            <div class="tagline">Aluminium Profile Extrusions &amp; Workstations</div>
            <div class="phone">CALL (03) 9706 9022</div>
            <div class="date">Report generated: ${new Date().toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' })}</div>
            ${preparedFor}
          </div>
        </div>

        <h1>Deflection Calculation Report</h1>
        <p class="subtitle">Prepared with the AME System deflection calculator — amesystem.com.au/pages/deflection-calculator</p>

        <div class="params">
          <div class="param"><div class="param-label">Profile</div><div class="param-value">${profile.id} <small>(${profile.size} mm)</small></div></div>
          <div class="param"><div class="param-label">Length (L)</div><div class="param-value">${L} mm</div></div>
          <div class="param"><div class="param-label">Load (F)</div><div class="param-value">${loadStr}</div></div>
          <div class="param"><div class="param-label">Orientation</div><div class="param-value">${orientStr} <small>I${state.axis} = ${I} cm⁴, W${state.axis} = ${W} cm³</small></div></div>
        </div>

        <div class="hero">
          <div>
            <div class="hero-scenario">${scenario.groupName} · ${scenario.name}</div>
            <div class="hero-diagram">
              <svg viewBox="0 0 200 100">${diagramMarkup(scenario, r.total)}</svg>
            </div>
            <div class="hero-legend">${state.unit === 'kg' ? `F = ${state.loadValue} kg (${Math.round(F)} N)` : `F = ${state.loadValue} N`} · L = ${L} mm · D = ${fmt(r.total)} mm</div>
          </div>
          <div>
            <div class="def-row">
              <span class="def-caption">It will sag about</span><br>
              <span class="def-value">${fmt(r.total)}</span> <span class="def-unit">mm</span>
            </div>
            <div class="verdict"><strong>${verdict.icon} ${verdict.title}</strong>${verdict.body}</div>
            <div class="guides">Sag guidelines for a ${L} mm span: precision work (L/500) = ${fmt(L / SAG_PRECISION)} mm · general use (L/250) = ${fmt(L / SAG_GENERAL)} mm</div>
            <div class="stress">${stressOk ? '✓' : '⚠'} Bending stress ${r.sigma.toFixed(1)} N/mm² — ${stressOk ? 'safely under' : 'above'} the ${YIELD_STRENGTH} N/mm² yield point</div>
          </div>
        </div>

        <div class="calc">
          <h3>Full calculation (includes profile self-weight)</h3>
          <p>${fLine}</p>
          <p>Weight force: F<sub>g</sub> = M·L·g = ${profile.M} kg/m × ${(L / 1000).toFixed(2)} m × ${GRAVITY} m/s² ≈ <strong>${r.fg.toFixed(2)} N</strong></p>
          <p>Deflection due to own weight: ${scenario.dgFormula} ≈ <strong>${r.dg.toFixed(2)} mm</strong></p>
          <p>Deflection due to the acting force: ${scenario.dfFormula} ≈ <strong>${r.df.toFixed(2)} mm</strong></p>
          <p>Total deflection: D = d<sub>g</sub> + d<sub>F</sub> ≈ <strong>${fmt(r.total)} mm</strong></p>
          <p class="note">The final deflection result includes the contribution from the profile's self-weight.</p>
        </div>

        <h2>All six load scenarios</h2>
        <table>
          <thead>
            <tr>
              <th>Load Case</th>
              <th style="text-align:center;">Own Weight d<sub>g</sub></th>
              <th style="text-align:center;">Acting Force d<sub>F</sub></th>
              <th style="text-align:center;">Total Deflection D</th>
              <th style="text-align:center;">Bending Stress σ</th>
              <th style="text-align:center;">Status</th>
            </tr>
          </thead>
          <tbody>${resultsHTML}</tbody>
        </table>
        <p class="selfweight-note">▶ marks your selected scenario. Total deflection D = d<sub>g</sub> + d<sub>F</sub> includes the profile's self-weight (F<sub>g</sub> = M·L·g).</p>

        <div class="specs">
          <h3>Profile Specifications — ${profile.id}</h3>
          <div class="specs-grid">
            <div class="spec-item"><strong>Ix:</strong> ${profile.Ix} cm⁴</div>
            <div class="spec-item"><strong>Iy:</strong> ${profile.Iy} cm⁴</div>
            <div class="spec-item"><strong>Wx:</strong> ${profile.Wx} cm³</div>
            <div class="spec-item"><strong>Wy:</strong> ${profile.Wy} cm³</div>
            <div class="spec-item"><strong>Area:</strong> ${profile.A} cm²</div>
            <div class="spec-item"><strong>Mass:</strong> ${profile.M} kg/m</div>
          </div>
        </div>

        <div style="text-align:center;">
          <a href="${BASE_URL}${profile.url}" class="buy-link">Configure &amp; Order This Profile →</a>
        </div>

        <div class="footer">
          <p class="brand"><strong>AME System</strong> | Aluminium Profile Extrusions &amp; Workstations — <strong>CALL (03) 9706 9022</strong></p>
          <p>E = 70,000 N/mm² (Aluminium) · g = 9.81 m/s² · Yield point = ${YIELD_STRENGTH} N/mm² · Results are theoretical estimates. Consult an engineer for critical applications.</p>
          <p>amesystem.com.au</p>
        </div>

        <div class="terms">
          <h3>Deflection Calculator T&amp;C</h3>
          <p>This calculator is intended as an engineering guide only. Results are estimates based on the information you provide and do not replace professional engineering assessment or project-specific verification.</p>
          <p>By using this calculator, you acknowledge that you are responsible for verifying all calculations and determining the suitability of your design. AME System Pty Ltd is not liable for any loss or damage resulting from the use of this calculator or reliance on its results.</p>
        </div>
      </body>
      </html>`;

    const printWindow = window.open('', '_blank');
    printWindow.document.write(printContent);
    printWindow.document.close();
    printWindow.onload = () => setTimeout(() => printWindow.print(), 200);
  }

  // ============ RECOMMENDER ============
  let recUnit = 'kg';

  function runRecommender() {
    const length = parseFloat(document.getElementById('dcv2-rec-length')?.value);
    const loadVal = parseFloat(document.getElementById('dcv2-rec-load')?.value);
    const maxDef = parseFloat(document.getElementById('dcv2-rec-maxdef')?.value);
    const scenario = scenarioById(document.getElementById('dcv2-rec-scenario')?.value) || scenarioById('central-supported');

    const resultsEl = document.getElementById('dcv2-rec-results');
    if (!resultsEl) return;

    if (!length || !loadVal || !maxDef) {
      resultsEl.innerHTML = '<p class="dcv2-rec-empty">Enter your requirements above to find matching profiles.</p>';
      return;
    }

    const F = recUnit === 'kg' ? loadVal * GRAVITY : loadVal;
    const results = [];
    PROFILES.forEach(profile => {
      ['x', 'y'].forEach(axis => {
        if (profile.Ix === profile.Iy && axis === 'y') return;
        const r = computeFor(scenario, profile, axis, F, length);
        if (r.total <= maxDef) {
          results.push({
            profile, axis,
            deflection: r.total,
            sigma: r.sigma,
            margin: ((maxDef - r.total) / maxDef * 100).toFixed(0)
          });
        }
      });
    });
    results.sort((a, b) => a.profile.M - b.profile.M);

    if (results.length === 0) {
      resultsEl.innerHTML = `
        <div class="dcv2-rec-empty">
          <p>No profiles found that meet your requirements.</p>
          <p style="font-size:12px;color:#88949f;margin-top:8px;">Try increasing max sag or reducing length/load.</p>
        </div>`;
      return;
    }

    let html = '<div class="dcv2-rec-list">';
    results.slice(0, 8).forEach((r, i) => {
      const ok = r.sigma <= YIELD_STRENGTH;
      const orient = r.profile.Ix === r.profile.Iy ? 'Any orientation' : (r.axis === 'x' ? 'Mounted flat' : 'Mounted upright');
      html += `
        <div class="dcv2-rec-item ${i === 0 ? 'dcv2-rec-best' : ''}">
          ${i === 0 ? '<span class="dcv2-rec-badge">Best match — lightest that works</span>' : ''}
          <div class="dcv2-rec-item-header">
            <span class="dcv2-rec-item-icon">${profileIcon(r.profile)}</span>
            <span>
              <span class="dcv2-rec-item-name">${r.profile.id}</span>
              <span class="dcv2-rec-item-sub">${r.profile.size} mm · ${orient}</span>
            </span>
          </div>
          <div class="dcv2-rec-item-stats">
            <div class="dcv2-rec-stat"><span class="dcv2-rec-stat-label">Sag D</span><span class="dcv2-rec-stat-value">${fmt(r.deflection)} mm</span></div>
            <div class="dcv2-rec-stat"><span class="dcv2-rec-stat-label">Stress σ</span><span class="dcv2-rec-stat-value">${r.sigma.toFixed(1)} N/mm²</span></div>
            <div class="dcv2-rec-stat"><span class="dcv2-rec-stat-label">Weight</span><span class="dcv2-rec-stat-value">${r.profile.M} kg/m</span></div>
            <div class="dcv2-rec-stat"><span class="dcv2-rec-stat-label">Margin</span><span class="dcv2-rec-stat-value dcv2-rec-margin">+${r.margin}%</span></div>
          </div>
          <div class="dcv2-rec-item-footer">
            <span class="dcv2-rec-pill ${ok ? 'dcv2-rec-pill-ok' : 'dcv2-rec-pill-warn'}">${ok ? '✓ Within yield strength' : '⚠ Exceeds yield strength'}</span>
            <span class="dcv2-rec-links">
              <button type="button" class="dcv2-rec-open" data-open-profile="${r.profile.id}" data-open-axis="${r.axis}" data-open-length="${length}" data-open-load="${loadVal}" data-open-scenario="${scenario.id}">Test in calculator</button>
              <a href="${BASE_URL}${r.profile.url}" target="_blank" rel="noopener" class="dcv2-rec-buy">View & order →</a>
            </span>
          </div>
        </div>`;
    });
    html += '</div>';
    if (results.length > 8) {
      html += `<p style="text-align:center;font-size:12px;color:#88949f;margin-top:15px;">${results.length - 8} more profiles also match your requirements.</p>`;
    }
    resultsEl.innerHTML = html;
  }

  // ============ TABS ============
  function switchTab(tabId) {
    document.querySelectorAll('.dcv2-tab').forEach(t => t.classList.remove('dcv2-tab-active'));
    document.querySelectorAll('.dcv2-tab-content').forEach(c => c.classList.remove('dcv2-tab-content-active'));
    document.querySelector(`.dcv2-tab[data-tab="${tabId}"]`)?.classList.add('dcv2-tab-active');
    document.getElementById(`dcv2-tab-${tabId}`)?.classList.add('dcv2-tab-content-active');
  }

  // ============ UNIT HANDLING ============
  function setUnitUI() {
    document.querySelectorAll('.dcv2-unit-btn').forEach(b => b.classList.toggle('dcv2-selected', b.dataset.unit === state.unit));
    const slider = document.getElementById('dcv2-load-slider');
    const input = document.getElementById('dcv2-load');
    if (slider) {
      if (state.unit === 'kg') { slider.min = 1; slider.max = 300; slider.step = 1; }
      else { slider.min = 10; slider.max = 3000; slider.step = 10; }
      slider.value = Math.min(state.loadValue, parseFloat(slider.max));
    }
    if (input) input.value = state.loadValue;
    const suffix = document.getElementById('dcv2-load-suffix');
    if (suffix) suffix.textContent = state.unit === 'kg' ? 'kg' : 'N';
  }

  function switchUnit(unit) {
    if (unit === state.unit) return;
    if (unit === 'kg') {
      state.loadValue = Math.round((state.loadValue / GRAVITY) * 10) / 10;
    } else {
      state.loadValue = Math.round(state.loadValue * GRAVITY);
    }
    state.unit = unit;
    setUnitUI();
    render();
  }

  // ============ INIT ============
  function init() {
    buildPresets();
    buildProfilePicker();
    buildLoadTypePicker();
    buildSupportPicker();
    buildOrientationPicker();
    buildScenarioGrid();

    // presets
    document.getElementById('dcv2-presets')?.addEventListener('click', e => {
      const btn = e.target.closest('.dcv2-preset');
      if (!btn) return;
      const preset = PRESETS.find(p => p.id === btn.dataset.preset);
      if (!preset) return;
      state.preset = preset.id;
      if (!preset.custom) {
        state.length = preset.length;
        state.loadValue = preset.kg;
        state.unit = 'kg';
        state.loadType = preset.loadType;
        state.support = preset.support;
        setUnitUI();
      }
      switchTab('calculator');
      render({ syncInputs: true });
      if (preset.custom) document.getElementById('dcv2-length')?.focus();
    });

    // profile picker
    document.getElementById('dcv2-profile-picker')?.addEventListener('click', e => {
      const btn = e.target.closest('.dcv2-profile-card');
      if (!btn) return;
      state.profileId = btn.dataset.profile;
      render();
    });

    // orientation
    document.getElementById('dcv2-orientation')?.addEventListener('click', e => {
      const btn = e.target.closest('.dcv2-orient');
      if (!btn) return;
      state.axis = btn.dataset.axis;
      render();
    });

    // load type & support
    document.getElementById('dcv2-loadtype')?.addEventListener('click', e => {
      const btn = e.target.closest('.dcv2-pick');
      if (!btn) return;
      state.loadType = btn.dataset.loadtype;
      state.preset = null;
      render();
    });
    document.getElementById('dcv2-supports')?.addEventListener('click', e => {
      const btn = e.target.closest('.dcv2-pick');
      if (!btn) return;
      state.support = btn.dataset.support;
      state.preset = null;
      render();
    });

    // length input + slider
    const lenInput = document.getElementById('dcv2-length');
    const lenSlider = document.getElementById('dcv2-length-slider');
    lenInput?.addEventListener('input', () => {
      const v = parseFloat(lenInput.value);
      if (v >= 100) {
        state.length = v;
        if (lenSlider) lenSlider.value = Math.min(v, parseFloat(lenSlider.max));
        state.preset = null;
        render();
      }
    });
    lenSlider?.addEventListener('input', () => {
      state.length = parseFloat(lenSlider.value);
      if (lenInput) lenInput.value = state.length;
      state.preset = null;
      render();
    });

    // load input + slider + unit
    const loadInput = document.getElementById('dcv2-load');
    const loadSlider = document.getElementById('dcv2-load-slider');
    loadInput?.addEventListener('input', () => {
      const v = parseFloat(loadInput.value);
      if (v > 0) {
        state.loadValue = v;
        if (loadSlider) loadSlider.value = Math.min(v, parseFloat(loadSlider.max));
        state.preset = null;
        render();
      }
    });
    loadSlider?.addEventListener('input', () => {
      state.loadValue = parseFloat(loadSlider.value);
      if (loadInput) loadInput.value = state.loadValue;
      state.preset = null;
      render();
    });
    document.querySelectorAll('.dcv2-unit-btn').forEach(b => {
      b.addEventListener('click', () => switchUnit(b.dataset.unit));
    });

    // suggestion "Try it" buttons (delegated)
    document.getElementById('dcv2-suggestion')?.addEventListener('click', e => {
      const btn = e.target.closest('[data-use-profile]');
      if (!btn) return;
      state.profileId = btn.dataset.useProfile;
      state.axis = btn.dataset.useAxis || 'x';
      render();
    });

    // scenario grid
    document.getElementById('dcv2-grid')?.addEventListener('click', e => {
      const btn = e.target.closest('.dcv2-scenario-card');
      if (!btn) return;
      const sc = scenarioById(btn.dataset.scenario);
      if (!sc) return;
      state.loadType = sc.group;
      state.support = sc.support;
      state.preset = null;
      render();
      document.getElementById('dcv2-result-panel')?.scrollIntoView({ behavior: REDUCED_MOTION ? 'auto' : 'smooth', block: 'nearest' });
    });

    // share + pdf (first PDF download asks for an email; the native Shopify
    // form then creates the tagged customer for the custom.t_c Flow and
    // round-trips back to the current calculation via return_to)
    document.getElementById('dcv2-copy-btn')?.addEventListener('click', copyShareURL);

    const TC_KEY = 'ame-deflection-terms-v2';
    const tcRecord = () => {
      try { return JSON.parse(localStorage.getItem(TC_KEY) || 'null'); } catch (e) { return null; }
    };
    const emailOverlay = document.getElementById('dcv2-email-overlay');
    const emailInput = document.getElementById('dcv2-email-input');
    const emailSubmit = document.getElementById('dcv2-email-submit');

    document.getElementById('dcv2-pdf-btn')?.addEventListener('click', () => {
      const rec = tcRecord();
      if ((rec && rec.email) || !emailOverlay || !emailInput) { exportPDF(); return; }
      emailOverlay.hidden = false;
      document.body.style.overflow = 'hidden';
      emailInput.focus();
    });

    if (emailOverlay && emailInput && emailSubmit) {
      const updateEmailBtn = () => {
        emailSubmit.disabled = !(emailInput.value.trim() !== '' && emailInput.checkValidity());
      };
      updateEmailBtn();
      emailInput.addEventListener('input', updateEmailBtn);

      document.getElementById('dcv2-email-close')?.addEventListener('click', () => {
        emailOverlay.hidden = true;
        document.body.style.overflow = '';
      });

      document.getElementById('dcv2-email-form')?.addEventListener('submit', e => {
        if (emailSubmit.disabled) { e.preventDefault(); return; }
        const rec = tcRecord() || { accepted: true, acceptedAt: new Date().toISOString(), page: location.pathname };
        rec.email = emailInput.value.trim();
        rec.emailAt = new Date().toISOString();
        try { localStorage.setItem(TC_KEY, JSON.stringify(rec)); } catch (err) {}
        // come back to this exact calculation after the Shopify round-trip
        const ret = document.getElementById('dcv2-email-return');
        const share = document.getElementById('dcv2-share-url');
        if (ret && share && share.value) {
          try { const u = new URL(share.value); ret.value = u.pathname + u.search; } catch (err) {}
        }
        // open the report within this click gesture, then let the native
        // (CAPTCHA-wired) form submission proceed
        exportPDF();
        emailOverlay.hidden = true;
        document.body.style.overflow = '';
      });
    }

    // recommender
    ['dcv2-rec-length', 'dcv2-rec-load', 'dcv2-rec-maxdef'].forEach(id => {
      const el = document.getElementById(id);
      if (el) {
        el.addEventListener('input', runRecommender);
        el.addEventListener('change', runRecommender);
      }
    });
    document.getElementById('dcv2-rec-scenario')?.addEventListener('change', runRecommender);
    document.querySelectorAll('.dcv2-rec-unit-btn').forEach(b => {
      b.addEventListener('click', () => {
        if (b.dataset.unit === recUnit) return;
        const input = document.getElementById('dcv2-rec-load');
        const v = parseFloat(input?.value);
        recUnit = b.dataset.unit;
        document.querySelectorAll('.dcv2-rec-unit-btn').forEach(x => x.classList.toggle('dcv2-selected', x.dataset.unit === recUnit));
        const suffix = document.getElementById('dcv2-rec-load-suffix');
        if (suffix) suffix.textContent = recUnit === 'kg' ? 'kg' : 'N';
        if (input && v > 0) {
          input.value = recUnit === 'kg' ? Math.round((v / GRAVITY) * 10) / 10 : Math.round(v * GRAVITY);
        }
        runRecommender();
      });
    });
    document.getElementById('dcv2-rec-results')?.addEventListener('click', e => {
      const btn = e.target.closest('[data-open-profile]');
      if (!btn) return;
      state.profileId = btn.dataset.openProfile;
      state.axis = btn.dataset.openAxis || 'x';
      state.length = parseFloat(btn.dataset.openLength) || state.length;
      state.loadValue = parseFloat(btn.dataset.openLoad) || state.loadValue;
      state.unit = recUnit;
      const sc = scenarioById(btn.dataset.openScenario);
      if (sc) { state.loadType = sc.group; state.support = sc.support; }
      state.preset = null;
      setUnitUI();
      switchTab('calculator');
      render({ syncInputs: true });
      document.getElementById('dcv2-main')?.scrollIntoView({ behavior: REDUCED_MOTION ? 'auto' : 'smooth' });
    });

    // tabs
    document.querySelectorAll('.dcv2-tab').forEach(tab => {
      tab.addEventListener('click', () => switchTab(tab.dataset.tab));
    });

    // initial state: URL params or the default worked example
    loadFromURL();
    setUnitUI();
    render({ syncInputs: true });

    // keep the chart crisp when the layout width changes
    let resizeTimer = null;
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => render(), 200);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
